import { useAxios } from '@/contexts/AxiosProvider'
import { Page, Row } from '@/contexts/FlowNotesProvider'
import { useHall } from '@/contexts/HallProvider'
import { useTurns } from '@/contexts/TurnsProvider'
import { TurnType } from '@db8/shared'

const normalize = (str: string) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '')

// Map TurnType to speech type strings for LD and Quick Round format
const mapTurnTypeToSpeechType = (turnType: TurnType, step: number, format: string): string => {
  // Normalize format string to ignore spaces/case
  const normalizedFormat = format.replace(/\s/g, '').toLowerCase()
  if (normalizedFormat === 'quickround') {
    // Quick Round: 0=1AC, 1=1NC, 2=1AR, 3=2AR
    return (step + 1).toString()
  }
  switch (turnType) {
    case TurnType.Affirmative:
      if (step === 0) return '1AC'
      if (step === 6) return '1AR'
      if (step === 10) return '2AR'
      return '1AC' // fallback
    case TurnType.Negative:
      if (step === 3) return '1NC'
      if (step === 8) return 'NR'
      return '1NC' // fallback
    case TurnType.AffirmativeCrossNegative:
      return 'CX_AxN'
    case TurnType.NegativeCrossAffirmative:
      return 'CX_NxA'
    default:
      return '1AC' // fallback
  }
}

export const useFlowNoteFetch = ({
  addRow,
  updateRow,
  aiJudgePage,
}: {
  addRow: Function
  updateRow: Function
  aiJudgePage: Page | undefined
}) => {
  const { transcriptions, hall } = useHall()
  const { currentTurn } = useTurns()
  const axios = useAxios()

  const parseDebateSections = (markdown: string) => {
    const sections: Array<{ title: string; text: string }> = []

    const sectionRegex = /\*\*(.*?):\*\*([\s\S]*?)(?=\*\*|$)/g
    let match: RegExpExecArray | null

    while ((match = sectionRegex.exec(markdown)) !== null) {
      const title = match[1].trim()
      const body = match[2].trim()

      // Handle framework sections that contain both value and criterion
      if (title.toLowerCase().includes('framework')) {
        // Try to split framework into value and criterion
        const valueMatch = body.match(/\*\*Value:\*\*\s*(.*?)(?=\*\*Criterion:\*\*|\*\*|$)/s)
        const criterionMatch = body.match(/\*\*Criterion:\*\*\s*(.*?)(?=\*\*|$)/s)

        if (valueMatch) {
          sections.push({ title: 'Value:', text: valueMatch[1].trim() })
        }
        if (criterionMatch) {
          sections.push({ title: 'Criterion:', text: criterionMatch[1].trim() })
        }

        // If no sub-sections found, keep the original framework section
        if (!valueMatch && !criterionMatch) {
          sections.push({ title, text: body })
        }
      } else {
        sections.push({ title, text: body })
      }
    }

    return sections
  }

  const findMatchingRow = (sectionTitle: string, rows: Row[]): Row | undefined => {
    const normalizedSectionTitle = normalize(sectionTitle)

    // First try exact match
    let match = rows.find((row) => normalize(row.title) === normalizedSectionTitle)
    if (match) return match

    // Then try partial matches with common variations
    const sectionTitleLower = sectionTitle.toLowerCase()

    // Handle framework sections that might contain both value and criterion
    if (sectionTitleLower.includes('framework')) {
      // Framework sections should be split into value and criterion
      return undefined // Let it create a new row
    }

    // Handle common variations
    if (sectionTitleLower.includes('resolution')) {
      match = rows.find((row) => row.title.toLowerCase().includes('resolution'))
    } else if (sectionTitleLower.includes('definition')) {
      match = rows.find((row) => row.title.toLowerCase().includes('definition'))
    } else if (sectionTitleLower.includes('value') && !sectionTitleLower.includes('clash')) {
      match = rows.find(
        (row) =>
          row.title.toLowerCase().includes('value') && !row.title.toLowerCase().includes('clash'),
      )
    } else if (sectionTitleLower.includes('criterion')) {
      match = rows.find((row) => row.title.toLowerCase().includes('criterion'))
    } else if (sectionTitleLower.includes('contention')) {
      match = rows.find((row) => row.title.toLowerCase().includes('contention'))
    }

    return match
  }

  const writeBasicNotes = (columnId: number, transcriptText: string) => {
    if (!aiJudgePage) return
    const targetColumn = aiJudgePage.columns.find((c) => c.id === columnId)
    if (!targetColumn) return

    const existing =
      targetColumn.rows.find((r) => normalize(r.title) === normalize('Basic Notes:')) || undefined

    const clipped = (transcriptText || '').trim().slice(0, 3500)
    const content =
      clipped.length > 0
        ? clipped
        : 'No transcript captured for this turn.'

    if (existing) {
      updateRow(aiJudgePage.id, columnId, existing.id, { ...existing, content })
    } else {
      addRow(aiJudgePage.id, columnId, {
        id: `row-basic-${Date.now()}-${Math.random()}`,
        title: 'Basic Notes:',
        content,
      })
    }
  }

  const fetchFlowNote = async (opts?: { transcript?: string; step?: number; turnType?: TurnType }) => {
    // Get the current speech type and transcript for the current turn (allow overrides to avoid race conditions)
    const turnType = opts?.turnType ?? currentTurn?.type
    const step = opts?.step ?? currentTurn?.step
    const overrideTranscript = opts?.transcript
    const turnTranscripts = transcriptions[step] || []
    const transcript = (overrideTranscript && overrideTranscript.trim() !== '')
      ? overrideTranscript
      : turnTranscripts.map((t) => t.text).join('\n')
    const normalizedFormat = hall.format.replace(/\s/g, '').toLowerCase()
    const isLD = normalizedFormat === 'lincolndouglas'
    const isQR = normalizedFormat === 'quickround'
    // Map turn type to speech type for LD and Quick Round format
    const speechType =
      isLD || isQR ? mapTurnTypeToSpeechType(turnType, step, hall.format) : turnType

    // no-op
    if (!transcriptions || !transcript) return

    const columnId = typeof step === 'number' ? step : parseInt(step as any, 10)

    try {
      // Backend supports per-speech transcript prompts only for LD/Quick Round.
      // For other formats, always send full transcriptions (otherwise backend returns 400).
      const res = await axios.post('/flowNotes/generate', {
        format: isLD ? 'LincolnDouglas' : isQR ? 'Quick Round' : hall.format,
        ...(isLD || isQR ? { speechType, transcript } : { transcriptions }),
      })

      const parsedResponse = parseDebateSections(res.data.text || '')
      if (!parsedResponse || parsedResponse.length === 0) {
        writeBasicNotes(columnId, transcript)
        return parsedResponse
      }

      const targetColumn = aiJudgePage?.columns.find((c) => c.id === columnId)
      if (!targetColumn) return parsedResponse

      parsedResponse
        .filter(
          (section) =>
            section.text.trim() !== '' && section.text.trim().toLowerCase() !== 'not provided',
        )
        .forEach((section) => {
          const existingRow = findMatchingRow(section.title, targetColumn.rows)

          if (existingRow) {
            updateRow(aiJudgePage.id, columnId, existingRow.id, {
              ...existingRow,
              content: section.text,
            })
          } else {
            const newRow: Row = {
              id: `row-${Date.now()}-${Math.random()}`,
              title: section.title,
              content: section.text,
            }
            addRow(aiJudgePage.id, columnId, newRow)
          }
        })

      return parsedResponse
    } catch (e) {
      // Fallback: always write at least basic notes to AI Flow Notes.
      writeBasicNotes(columnId, transcript)
      return []
    }
  }

  return {
    fetchFlowNote,
  }
}
